import { useState, useEffect } from "react";
import debounce from "lodash.debounce";

export default function CollegePicker({ value = [], onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Fetch colleges via autocomplete API
  const fetchColleges = async (search) => {
    if (!search || search.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/colleges/autocomplete?query=${encodeURIComponent(search)}&limit=20`
      );
      const data = await res.json();
      setResults(data.results || []);
    } catch (err) {
      console.error("Autocomplete error:", err);
      setResults([]);
    }

    setLoading(false);
  };

  // Debounced search
  const debouncedSearch = debounce((val) => fetchColleges(val), 300);

  useEffect(() => {
    debouncedSearch(query);
    return debouncedSearch.cancel;
  // eslint-disable-next-line
  }, [query]);


  // Add selected college
  const addCollege = (college) => {
    if (value.length >= 5) return; // limit
    if (value.some((c) => c.id === college.id)) return; // no duplicates

    const updated = [...value, college];
    onChange(updated);

    setQuery("");
    setResults([]);
    setOpen(false);
  };

  // Remove selected college
  const removeCollege = (collegeId) => {
    const updated = value.filter((c) => c.id !== collegeId);
    onChange(updated);
  };


  return (
    <div className="w-full">
      <label className="block mb-2 text-sm font-medium" style={{ color: '#2A2F35' }}>
        Colleges You&apos;re Considering (up to 5)
      </label>

      {/* Selected colleges as chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {value.map((college) => (
            <div
              key={college.id}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium"
              style={{
                backgroundColor: '#F0FDF4',
                color: '#004C3F',
                border: '1px solid #004C3F',
                borderRadius: '8px'
              }}
            >
              {college.name}
              <button
                onClick={() => removeCollege(college.id)}
                className="text-sm font-bold"
                style={{ color: '#004C3F' }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          placeholder="Start typing a college name…"
          className="w-full text-sm transition-all pl-4 pr-4"
          style={{
            height: '48px',
            border: '1px solid #E1E4E8',
            borderRadius: '12px',
            color: '#2A2F35',
            backgroundColor: 'white',
            outline: 'none',
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={(e) => {
            e.target.style.border = '2px solid #004C3F';
            setOpen(true);
          }}
          onBlur={(e) => {
            setTimeout(() => {
              e.target.style.border = '1px solid #E1E4E8';
              setOpen(false);
            }, 200);
          }}
        />

        {/* Results dropdown */}
        {open && query.length >= 2 && (
          <div
            className="absolute z-10 w-full mt-1 bg-white shadow-lg"
            style={{
              border: '1px solid #E1E4E8',
              borderRadius: '12px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            {loading && (
              <div className="p-4 text-gray-500 text-sm">Searching…</div>
            )}

            {!loading && results.length === 0 && (
              <div className="p-4 text-gray-500 text-sm">No results found</div>
            )}

            {!loading && results.map((college) => (
              <div
                key={college.id}
                onClick={() => addCollege(college)}
                className="px-4 py-3 cursor-pointer hover:bg-gray-50"
              >
                <div className="font-medium text-sm" style={{ color: '#2A2F35' }}>
                  {college.name}
                </div>
                <div className="text-xs" style={{ color: '#6B7280' }}>
                  {college.city}, {college.state}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
